import type { Key } from '@lichess-org/chessground/types';

/** Pure keyboard-navigation helpers for the board (WCAG 2.2 AA keyboard support). */

export type Direction = 'up' | 'down' | 'left' | 'right';

export function fileOf(key: Key): number {
  return key.charCodeAt(0) - 97; // 'a' -> 0
}

export function rankOf(key: Key): number {
  return key.charCodeAt(1) - 49; // '1' -> 0
}

function keyFrom(file: number, rank: number): Key {
  return `${String.fromCharCode(97 + file)}${rank + 1}` as Key;
}

/**
 * Move a square one step in a direction, honoring board orientation (so the
 * "up" arrow always means "toward the opponent" from the viewer's perspective).
 * Stays put at the board edge.
 */
export function moveKey(key: Key, direction: Direction, orientation: 'white' | 'black'): Key {
  let file = fileOf(key);
  let rank = rankOf(key);
  switch (direction) {
    case 'up':
      rank += orientation === 'white' ? 1 : -1;
      break;
    case 'down':
      rank += orientation === 'white' ? -1 : 1;
      break;
    case 'left':
      file += orientation === 'white' ? -1 : 1;
      break;
    case 'right':
      file += orientation === 'white' ? 1 : -1;
      break;
  }
  if (file < 0 || file > 7 || rank < 0 || rank > 7) return key;
  return keyFrom(file, rank);
}

/** Map a browser Arrow* key to a board direction, or null. */
export function directionFromKey(key: string): Direction | null {
  switch (key) {
    case 'ArrowUp':
      return 'up';
    case 'ArrowDown':
      return 'down';
    case 'ArrowLeft':
      return 'left';
    case 'ArrowRight':
      return 'right';
    default:
      return null;
  }
}
